// Skills v2 library — pure formatting logic (2026-08-19).
//
// Zero React. Every user-facing string is outcome-first, no jargon. The mockup
// is the conformance spec: docs/design/skills-framework-v2-mockup.html screens
// #s5 (gallery + your skills) and #s6 (author). All shelf/table formatting
// lives here so the components stay thin and this file hits 100% coverage.

// 0.14 → "▲ +14%"; negative → "▼ -N%"; null/undefined/non-number → "" (no badge).
export function formatLift(fraction) {
  if (typeof fraction !== "number" || Number.isNaN(fraction)) return "";
  const pct = Math.round(fraction * 100);
  if (pct === 0) return "▲ +0%";
  return pct > 0 ? `▲ +${pct}%` : `▼ -${Math.abs(pct)}%`;
}

// data_fit → friendly shelf label.
export function dataFitLabel(dataFit) {
  if (dataFit === "works_with_data") return "✓ Works with your data";
  if (dataFit === "no_data_needed") return "no data needed";
  return "";
}

// your-skills health → pill {label, tone}. tone ∈ ok|warn|dim.
export function healthPill(health) {
  if (health === "healthy") return { label: "Healthy", tone: "ok" };
  if (health === "needs_look") return { label: "Needs a look", tone: "warn" };
  if (health === "paused") return { label: "Paused", tone: "warn" };
  if (health === "new") return { label: "New · measuring…", tone: "dim" };
  return { label: "Unknown", tone: "dim" };
}

// 4.2 → "★★★★☆" (full = round(avg), 0-5). null/non-number → "".
export function stars(avgRating) {
  if (typeof avgRating !== "number" || Number.isNaN(avgRating)) return "";
  let full = Math.round(avgRating);
  if (full < 0) full = 0;
  if (full > 5) full = 5;
  return "★".repeat(full) + "☆".repeat(5 - full);
}

// 312 → "312 installs"; 1 → "1 install"; 0/negative/non-number → "".
export function installLabel(n) {
  if (typeof n !== "number" || Number.isNaN(n) || n <= 0) return "";
  return `${n} install${n === 1 ? "" : "s"}`;
}

// impact_pct is a PERCENT already (the backend's your_skills returns
// round(accuracy_delta * 100), e.g. 14), NOT a fraction — render it directly so
// 14 → "▲ +14%" (a prior bug fed it through formatLift and showed 1400%).
// null/undefined/non-number → "◷ measuring…".
export function impactLabel(impactPct) {
  if (typeof impactPct !== "number" || Number.isNaN(impactPct)) return "◷ measuring…";
  const rounded = Math.round(impactPct);
  return rounded >= 0 ? `▲ +${rounded}%` : `▼ ${Math.abs(rounded)}%`;
}

// agent_count → "N agents" / "1 agent"; 0/non-number → "no agents yet".
export function agentCountLabel(n) {
  if (typeof n !== "number" || Number.isNaN(n) || n <= 0) return "no agents yet";
  return `${n} agent${n === 1 ? "" : "s"}`;
}

// source → small badge label. "authored" → "Authored", else "Gallery".
export function sourceLabel(source) {
  return source === "authored" ? "Authored" : "Gallery";
}

// Derive the category chip row from a gallery result set: ["All", ...distinct].
// Preserves first-seen order; ignores blank/missing categories.
export function deriveCategories(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const seen = [];
  for (const r of list) {
    const c = r && typeof r.category === "string" ? r.category.trim() : "";
    if (c && !seen.includes(c)) seen.push(c);
  }
  return ["All", ...seen];
}

// tone → hex, with the memory-rule fallbacks (no bare COLORS.red / COLORS.green).
export function toneColor(tone, colors) {
  const c = colors || {};
  if (tone === "ok") return c.green || "#10b981";
  if (tone === "warn") return c.orange || "#f59e0b";
  if (tone === "bad") return c.red || "#ef4444";
  return c.textDim || "#9090a8";
}

// A scripts (executable) skill's review state → a badge {label, tone}. Guidance
// skills return null (no code, no review). Used by the executable marketplace.
export function reviewStateBadge(state) {
  switch (state) {
    case "approved": return { label: "Reviewed", tone: "ok" };
    case "stale": return { label: "Re-review needed", tone: "warn" };
    case "pending": return { label: "Review pending", tone: "warn" };
    case "rejected": return { label: "Rejected", tone: "bad" };
    case "unreviewed": return { label: "Awaiting review", tone: "warn" };
    default: return null;
  }
}

// Whether a run of this skill can actually execute yet (published + reviewed).
// Purely informational in the gallery (no gating) — tells the user what to expect.
export function isRunnable(skill) {
  return !!skill && skill.tier === "scripts" && skill.review_state === "approved";
}

// Post-import toast. Skills are stored/found under the `name:` INSIDE the file
// (the frontmatter), not the filename — a common "where did my import go?" trap
// (staging: `Test_SKILL.md` declared `name: user-profile-test`, so it landed as
// that draft). Name the skill by its real name and, when the uploaded filename
// differs, call it out explicitly.
export function importedSkillNotice(row, fileName) {
  const display = (row && (row.display_name || row.name)) || "skill";
  const name = (row && row.name) || "";
  const shown = name && name !== display ? `“${display}” (${name})` : `“${display}”`;
  let msg = `Imported as ${shown} — it's a draft under that name in Developer → Base Skills. Review, then publish.`;
  if (fileName && name) {
    const base = String(fileName).replace(/\.(md|zip|txt)$/i, "").trim();
    if (base && base.toLowerCase() !== name.toLowerCase()) {
      msg += ` Note: the file was “${fileName}”, but skills are identified by the “name:” inside them — this one is “${name}”, so look for that.`;
    }
  }
  return msg;
}

// Parse an EXPORTED instance bundle so the "Install instance" form can auto-fill
// the otherwise-cryptic fields — the user then only picks the target agent.
// The export names the file `skill_<base_name>_<schema_hash>.md` and its body is
// a standard SKILL.md whose frontmatter carries `name:` (== base_name) and
// `version:`. Returns { base_name, base_version, schema_hash } (missing pieces
// come back as ""), so the caller can show what was detected and fall back to
// manual entry for anything absent.
export function parseInstanceBundle(fileName, body) {
  const out = { base_name: "", base_version: "", schema_hash: "" };
  const fnMatch = /^skill_(.+)_([0-9a-fA-F]{6,})\.md$/.exec(String(fileName || "").trim());
  if (fnMatch) { out.base_name = fnMatch[1]; out.schema_hash = fnMatch[2].toLowerCase(); }
  const fm = /^---\s*\n([\s\S]*?)\n---/.exec(String(body || ""));
  if (fm) {
    const nm = /(?:^|\n)\s*name:\s*(.+?)\s*$/m.exec(fm[1]);
    const ver = /(?:^|\n)\s*version:\s*['"]?([^'"\n]+?)['"]?\s*$/m.exec(fm[1]);
    if (nm) out.base_name = nm[1].trim();          // frontmatter name wins over filename
    if (ver) out.base_version = ver[1].trim();
  }
  return out;
}
